import { useCallback, useState } from 'react'

// MIMS rejects a drug search term under 3 characters, so no request is sent for one.
export const MIN_DRUG_SEARCH_LENGTH = 3
export const DEFAULT_DRUG_SEARCH_PAGE = { pageNumber: 1, pageOffset: 30 }

export interface DrugSearchRequest {
  queryTerm: string
  pageNumber: number
  pageOffset: number
}

export const isSearchableDrugTerm = (queryTerm: string): boolean =>
  queryTerm.trim().length >= MIN_DRUG_SEARCH_LENGTH

/**
 * Paging for the drug search results. The term can be shortened below 3 characters while the
 * results panel is still open, and the panel then asks for more results; `loadMore` sends
 * nothing in that case.
 */
export const useDrugSearchPaging = (
  searchTerm: string,
  searchProducts: (request: DrugSearchRequest) => unknown
) => {
  const [pageInfo, setPageInfo] = useState(DEFAULT_DRUG_SEARCH_PAGE)

  const loadMore = useCallback(() => {
    if (!isSearchableDrugTerm(searchTerm)) return
    const nextPage = { pageNumber: pageInfo.pageNumber + 1, pageOffset: pageInfo.pageOffset }
    setPageInfo(nextPage)
    searchProducts({ queryTerm: searchTerm, ...nextPage })
  }, [pageInfo, searchTerm, searchProducts])

  const resetPaging = useCallback(() => setPageInfo(DEFAULT_DRUG_SEARCH_PAGE), [])

  return { loadMore, resetPaging }
}
